import { GoldExpSystem } from '../goldExpSystem.js';
import { applyBlueBuff, applyRedBuff, transferBuffsOnKill } from '../jungle.js';

/**
 * KillRewardProcessor — Single Responsibility: distribuir recompensas (CS, oro, XP, buffs)
 * cuando una entidad muere a manos de un campeón.
 *
 * Contrato: processKillReward(victim, killer, opts?) → { goldReward, expReward, assistGold }
 * - Puro en efectos de lado controlados: sólo muta killer.cs, killer.gold vía GoldExpSystem,
 *   llama addExperience / awardNearbyExp y aplica buffs vía jungle.js.
 * - NO toca hp, isDead ni estado de muerte — eso le pertenece a Entity.takeDamage.
 */

const CS_ELIGIBLE_TYPES = new Set(['minion', 'turret', 'jungle']);
const CHAMPION_KILL_EXP = 210;

function isBlueGolem(victim) {
  return (
    victim.name === 'Ancient Golem' ||
    (typeof victim.id === 'string' && victim.id.includes('blue_golem'))
  );
}

function isRedLizard(victim) {
  return (
    victim.name === 'Lizard Elder' ||
    (typeof victim.id === 'string' && victim.id.includes('red_lizard'))
  );
}

function uniqueAssisters(assisters, killer) {
  const seen = new Set();
  const out = [];
  for (const a of assisters || []) {
    if (!a || a === killer || seen.has(a)) continue;
    seen.add(a);
    out.push(a);
  }
  return out;
}

function awardChampionAssistGold(victim, killer, assisters) {
  if (victim.type !== 'champion' || victim.bounty === 0) return 0;
  const unique = uniqueAssisters(assisters, killer);
  if (unique.length === 0) return 0;
  return GoldExpSystem.awardAssistGold(unique);
}

/** SPY-41: tally K/D/A on champion kills (mutates plain counters on entities). */
function recordChampionKda(victim, killer, assisters) {
  if (victim.type !== 'champion') return;
  if (victim.bounty === 0) return;
  killer.kills = (killer.kills || 0) + 1;
  victim.deaths = (victim.deaths || 0) + 1;
  for (const a of uniqueAssisters(assisters, killer)) {
    if (a.type !== 'champion') continue;
    a.assists = (a.assists || 0) + 1;
  }
}

/** SPY-103: champion kill XP via proximity share (no killer full + share double-credit). */
function awardChampionKillProximityExp(victim, killer, opts) {
  if (victim.bounty === 0) return 0;
  if (victim.nearbyExpProcessed) return 0;
  const radius = Number.isFinite(opts.xpRadius) ? opts.xpRadius : 8.0;
  const pool = Array.isArray(opts.nearbyChampions) && opts.nearbyChampions.length > 0
    ? opts.nearbyChampions
    : [killer];
  const withKiller = pool.includes(killer) ? pool : [...pool, killer];
  const ordered = withKiller[0] === killer
    ? withKiller
    : [killer, ...withKiller.filter((c) => c !== killer)];
  const awarded = GoldExpSystem.awardNearbyExp(ordered, victim, radius);
  if (awarded > 0) return CHAMPION_KILL_EXP;
  if (!killer || killer.isDead || typeof killer.addExperience !== 'function') return 0;
  victim.nearbyExpProcessed = true;
  killer.addExperience(CHAMPION_KILL_EXP);
  return CHAMPION_KILL_EXP;
}

function awardNonMinionExp(victim, killer, opts) {
  if (victim.type === 'champion') {
    return awardChampionKillProximityExp(victim, killer, opts);
  }
  const expReward = victim.exp !== undefined && victim.exp > 0 ? victim.exp : 35;
  if (expReward > 0 && typeof killer.addExperience === 'function') {
    killer.addExperience(expReward);
  }
  return expReward;
}

function applyKillBuffTransfers(victim, killer) {
  if (isBlueGolem(victim)) applyBlueBuff(killer, 150);
  else if (isRedLizard(victim)) applyRedBuff(killer, 150);
  // SPY-903: zero-bounty champion kills skip buff transfer (KDA already skipped).
  if (victim.type === 'champion' && victim.bounty === 0) return;
  if (victim.type === 'champion' && Array.isArray(victim.buffs) && victim.buffs.length > 0) {
    transferBuffsOnKill(victim, killer);
  }
}

/**
 * @param {object} victim
 * @param {object} killer
 * @param {{ assisters?: object[], nearbyChampions?: object[], xpRadius?: number }} [opts]
 * @returns {{ goldReward: number, expReward: number, assistGold: number }}
 */
export function processKillReward(victim, killer, opts = {}) {
  if (!killer || killer.type !== 'champion') {
    return { goldReward: 0, expReward: 0, assistGold: 0 };
  }

  // SPY-1103: non-positive bounty champions short-circuit (0/null/NaN/negative).
  if (victim.type === 'champion' && !(Number(victim.bounty) > 0)) {
    return { goldReward: 0, expReward: 0, assistGold: 0 };
  }

  const isMinion =
    typeof victim.type === 'string' && victim.type.startsWith('minion');
  if (isMinion || CS_ELIGIBLE_TYPES.has(victim.type)) {
    killer.cs = (killer.cs || 0) + 1;
  }

  const goldReward = GoldExpSystem.awardKillBounty(killer, victim);
  if (goldReward > 0 && typeof killer.onKillCallback === 'function') {
    killer.onKillCallback(victim, goldReward);
  }

  recordChampionKda(victim, killer, opts.assisters);
  const assistGold = awardChampionAssistGold(victim, killer, opts.assisters);
  const expReward = isMinion ? 0 : awardNonMinionExp(victim, killer, opts);
  applyKillBuffTransfers(victim, killer);

  return { goldReward, expReward, assistGold };
}
